// Widget Label Helper Functions
// This file provides utility functions for working with widget labels and styles

import { WIDGET_LABEL_DEFINITIONS } from './widgetLabelDefinitions';
import { resolveCategoryStatusColor } from './dynamicColorResolver';

/**
 * Get label value with fallback to default
 * @param {Object} config - Widget config object
 * @param {string} widgetType - Type of widget
 * @param {string} labelPath - Path to label (e.g., 'title', 'columnHeaders')
 * @returns {string|Array} - Label value or default
 */
export const getLabel = (config, widgetType, labelPath) => {
    const definition = WIDGET_LABEL_DEFINITIONS[widgetType];
    if (!definition) return '';

    const labelDef = definition.labels[labelPath];
    if (!labelDef) return '';

    const defaultValue = labelDef.default;
    const customValue = config?.labels?.[labelPath];

    return customValue !== undefined ? customValue : defaultValue;
};

/**
 * Get label style with fallback to default
 * @param {Object} config - Widget config object
 * @param {string} labelTarget - Style target (e.g., 'title', 'columnHeaders')
 * @param {Object} defaultStyles - Default styles to apply
 * @param {string} widgetType - Widget type to get default styles from definitions
 * @param {Object} data - Optional scan data for dynamic color resolution
 * @returns {Object} - Merged styles
 */
export const getLabelStyle = (config, labelTarget, defaultStyles = {}, widgetType = null, data = null) => {
    // Get default styles from widget definition if widgetType is provided
    let definitionDefaults = {};
    if (widgetType) {
        const definition = WIDGET_LABEL_DEFINITIONS[widgetType];
        definitionDefaults = definition?.defaultStyles?.[labelTarget] || {};
    }

    const customStyles = config?.labelStyles?.[labelTarget] || {};
    const mergedStyles = { ...defaultStyles, ...definitionDefaults, ...customStyles };

    // Resolve dynamic colors if data is provided
    if (data) {
        if (typeof mergedStyles.color === 'string' && mergedStyles.color.startsWith('dynamic:statusColor:')) {
            const category = mergedStyles.color.split(':')[2];
            mergedStyles.color = resolveCategoryStatusColor(category, data);
        }
        if (typeof mergedStyles.backgroundColor === 'string' && mergedStyles.backgroundColor.startsWith('dynamic:statusColor:')) {
            const category = mergedStyles.backgroundColor.split(':')[2];
            mergedStyles.backgroundColor = resolveCategoryStatusColor(category, data);
        }
    }

    return mergedStyles;
};


/**
 * Apply label styles to base styles
 * @param {Object} baseStyles - Base styles object
 * @param {Object} labelStyles - Label-specific styles
 * @returns {Object} - Merged styles
 */
export const applyLabelStyles = (baseStyles, labelStyles) => {
    if (!labelStyles) return baseStyles;

    return {
        ...baseStyles,
        fontSize: labelStyles.fontSize || baseStyles.fontSize,
        fontWeight: labelStyles.fontWeight || baseStyles.fontWeight,
        color: labelStyles.color || baseStyles.color,
        fontFamily: labelStyles.fontFamily || baseStyles.fontFamily,
        textAlign: labelStyles.textAlign || baseStyles.textAlign,
        lineHeight: labelStyles.lineHeight || baseStyles.lineHeight,
        letterSpacing: labelStyles.letterSpacing || baseStyles.letterSpacing,
        textTransform: labelStyles.textTransform || baseStyles.textTransform
    };
};

/**
 * Get all labels for a widget type
 * @param {string} widgetType - Type of widget
 * @returns {Object} - Object containing all label definitions
 */
export const getWidgetLabels = (widgetType) => {
    const definition = WIDGET_LABEL_DEFINITIONS[widgetType];
    return definition?.labels || {};
};

/**
 * Get all style targets for a widget type
 * @param {string} widgetType - Type of widget
 * @returns {Array} - Array of style target names
 */
export const getWidgetStyleTargets = (widgetType) => {
    const definition = WIDGET_LABEL_DEFINITIONS[widgetType];
    return definition?.styleTargets || [];
};

/**
 * Check if a widget type has label definitions
 * @param {string} widgetType - Type of widget
 * @returns {boolean} - True if widget has label definitions
 */
export const hasLabelDefinitions = (widgetType) => {
    return !!WIDGET_LABEL_DEFINITIONS[widgetType];
};

/**
 * Get default label value
 * @param {string} widgetType - Type of widget
 * @param {string} labelPath - Path to label
 * @returns {string|Array} - Default label value
 */
export const getDefaultLabel = (widgetType, labelPath) => {
    const definition = WIDGET_LABEL_DEFINITIONS[widgetType];
    if (!definition) return '';

    const labelDef = definition.labels[labelPath];
    return labelDef?.default || '';
};

/**
 * Check if a label is editable
 * @param {string} widgetType - Type of widget
 * @param {string} labelPath - Path to label
 * @returns {boolean} - True if label is editable
 */
export const isLabelEditable = (widgetType, labelPath) => {
    const definition = WIDGET_LABEL_DEFINITIONS[widgetType];
    if (!definition) return false;

    const labelDef = definition.labels[labelPath];
    return labelDef?.editable !== false; // Default to true if not specified
};

/**
 * Get default styles for a label target
 * @param {string} widgetType - Type of widget
 * @param {string} labelTarget - Style target
 * @returns {Object} - Default styles object
 */
export const getDefaultStyles = (widgetType, labelTarget) => {
    const definition = WIDGET_LABEL_DEFINITIONS[widgetType];
    return definition?.defaultStyles?.[labelTarget] || {};
};

const widgetLabelHelpers = {
    getLabel,
    getLabelStyle,
    applyLabelStyles,
    getWidgetLabels,
    getWidgetStyleTargets,
    hasLabelDefinitions,
    getDefaultLabel,
    isLabelEditable,
    getDefaultStyles
};

export default widgetLabelHelpers;
